/**
 * LandingPage — Minimal first viewport + atmospheric below-fold.
 * Device-neutral copy; scroll reveals; decorative motif only on landing.
 */
import React, { useEffect, useRef, useState } from 'react';
import StatementDropzone from './StatementDropzone';
import PasswordModal from './PasswordModal';
import WaitlistForm from './WaitlistForm';
import PwaChrome from './PwaChrome';
import { isStandaloneDisplay, isIosSafari } from '../utils/pwa';

function useRevealOnScroll(rootRef) {
    useEffect(() => {
        const root = rootRef.current;
        if (!root) return undefined;
        const nodes = root.querySelectorAll('[data-reveal]');
        if (!nodes.length) return undefined;

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-revealed');
                        io.unobserve(entry.target);
                    }
                });
            },
            { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
        );

        nodes.forEach((n) => io.observe(n));
        return () => io.disconnect();
    }, [rootRef]);
}

function LandingPage({ onFileDrop, onTrySample, passwordRequest, setPasswordRequest, status }) {
    const [openFaq, setOpenFaq] = useState(null);
    const [installHelp, setInstallHelp] = useState(false);
    const rootRef = useRef(null);
    useRevealOnScroll(rootRef);

    const faqs = [
        {
            q: 'Does my statement leave this device?',
            a: 'No. PDFs are parsed in the browser. Transaction lists stay on this device — we don’t upload statements for billing.',
        },
        {
            q: 'What’s Free vs Pro?',
            a: 'Free: ~30 days, no account, fully local. Pro (₹499 early / ₹999 lifetime): full history that merges on re-upload, encrypted vault, optional reminders. Pay once with Google.',
        },
        {
            q: 'Which apps are supported?',
            a: 'PhonePe, Google Pay, and Paytm PDFs. Cash or card spends can be added manually in the app.',
        },
        {
            q: 'What if the PDF is password protected?',
            a: 'Enter it in the local modal (often phone number or PAN). Never saved or sent anywhere.',
        },
        {
            q: 'Can I install Spenderly?',
            a: 'Yes — it’s a PWA. Use Install, or on iPhone: Safari → Share → Add to Home Screen.',
        },
        {
            q: 'How do updates work?',
            a: 'When you’re online, an Update now bar appears for new versions. Your data stays on this device.',
        },
    ];

    const openInstall = () => {
        if (isStandaloneDisplay()) return;
        setInstallHelp(true);
        window.dispatchEvent(new CustomEvent('spenderly:request-install'));
    };

    return (
        <div className="landing-root" ref={rootRef}>
            <div className="landing-atmosphere" aria-hidden="true">
                <div className="landing-grid" />
                <div className="landing-orb landing-orb--a" />
                <div className="landing-orb landing-orb--b" />
            </div>

            <nav className="landing-nav">
                <span className="landing-logo">spenderly</span>
                <button type="button" className="landing-nav-link" onClick={onTrySample}>
                    Try sample
                </button>
            </nav>

            <section className="landing-hero">
                <div className="landing-hero-inner landing-hero-enter">
                    <h1 className="landing-headline">Where did this month&apos;s salary go?</h1>
                    <p className="landing-support">
                        Drop your UPI statement PDF. Parsed on this device only.
                    </p>

                    <StatementDropzone
                        onFile={onFileDrop}
                        statusText={status?.active ? status.text : ''}
                        showSourcePicker={false}
                    />
                </div>
            </section>

            <section className="landing-below">
                <div className="landing-block" data-reveal>
                    <h2 className="landing-section-title">Why the salary vanishes by mid-month</h2>
                    <p className="landing-section-body">
                        UPI makes ₹40 chai and ₹180 Blinkit feel like nothing — until they add up.
                    </p>
                </div>

                <div className="landing-plans landing-block" data-reveal>
                    <h2 className="landing-section-title">Free vs Pro</h2>
                    <p className="landing-section-body" style={{ marginBottom: 20 }}>
                        Free covers a month’s audit. Pro keeps the years and a vault you move yourself.
                    </p>
                    <div className="landing-plan-grid">
                        <div className="landing-plan">
                            <p className="landing-plan-label">Free</p>
                            <p className="landing-plan-price">₹0 · no account</p>
                            <ul>
                                <li>On-device PDF parse</li>
                                <li>~30 days of history</li>
                                <li>Leak caps · safe-to-spend · CSV</li>
                                <li>Remembered merchants</li>
                                <li>Manual cash / card entries</li>
                            </ul>
                        </div>
                        <div className="landing-plan landing-plan--pro">
                            <p className="landing-plan-label">Pro lifetime</p>
                            <p className="landing-plan-price">₹499 early · then ₹999</p>
                            <ul>
                                <li>Everything in Free</li>
                                <li>Full history · re-uploads merge (no duplicates)</li>
                                <li>Encrypted vault (you hold the password)</li>
                                <li>Optional quiet reminders</li>
                                <li>One Google payment · activate once</li>
                            </ul>
                        </div>
                    </div>
                    <p className="landing-plan-note">
                        Statements stay on device. Pro account: email, payment ids, reminder prefs only.
                        {' '}<a href="/privacy.html">Privacy</a> · <a href="/terms.html">Terms</a>
                    </p>
                </div>

                <div className="landing-install-block landing-block" data-reveal>
                    <h3>Install</h3>
                    <p>
                        Add to home screen or desktop (PWA). Online updates show an <strong>Update now</strong> bar.
                    </p>
                    {!isStandaloneDisplay() && (
                        <button type="button" className="sp-btn-primary" onClick={openInstall}>
                            Install Spenderly
                        </button>
                    )}
                    {isStandaloneDisplay() && (
                        <p className="landing-trust">Already installed on this device.</p>
                    )}
                </div>

                <div className="landing-waitlist landing-block" data-reveal>
                    <h3>Get a ping for Pro</h3>
                    <p>
                        Prefer email before you buy? Leave yours — checkout is also in Profile after you start.
                    </p>
                    <WaitlistForm source="landing" />
                </div>

                <div className="landing-faq landing-block" data-reveal>
                    <h3>FAQ</h3>
                    {faqs.map((f, i) => (
                        <button
                            key={f.q}
                            type="button"
                            className={`landing-faq-item ${openFaq === i ? 'open' : ''}`}
                            onClick={() => setOpenFaq(openFaq === i ? null : i)}
                        >
                            <span>{f.q}</span>
                            {openFaq === i && <p>{f.a}</p>}
                        </button>
                    ))}
                </div>

                <footer className="landing-footer landing-block" data-reveal>
                    <span>spenderly.in</span>
                    <span className="legal-links">
                        <a href="/privacy.html">Privacy</a>
                        {' · '}
                        <a href="/terms.html">Terms</a>
                    </span>
                    <span>Local-first UPI spending</span>
                </footer>
            </section>

            <PwaChrome variant="landing" />

            {installHelp && (
                <div className="sp-modal-overlay" role="presentation" onClick={() => setInstallHelp(false)}>
                    <div
                        className="sp-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="landing-install-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 id="landing-install-title" style={{ margin: '0 0 8px', fontSize: 20 }}>
                            Install Spenderly
                        </h3>
                        {isIosSafari() ? (
                            <ol className="pwa-ios-steps">
                                <li>Tap <strong>Share</strong> in Safari</li>
                                <li>Choose <strong>Add to Home Screen</strong></li>
                                <li>Tap <strong>Add</strong></li>
                            </ol>
                        ) : (
                            <p className="sp-modal-message" style={{ marginBottom: 16 }}>
                                Use the <strong>Install now</strong> bar at the bottom when it appears,
                                or your browser menu → <strong>Install app</strong> / <strong>Add to Home screen</strong>.
                                On iPhone, open spenderly.in in Safari.
                            </p>
                        )}
                        <div className="sp-modal-actions">
                            <button type="button" className="sp-btn-primary" onClick={() => setInstallHelp(false)}>
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {passwordRequest && (
                <PasswordModal
                    title={passwordRequest.reason === 1 ? 'Statement is locked' : 'Incorrect password'}
                    message={passwordRequest.reason === 1
                        ? 'Enter password (processed strictly on device). Often your phone number or PAN.'
                        : 'That password was incorrect. Try again.'}
                    onSubmit={(pwd) => {
                        passwordRequest.callback(pwd);
                        setPasswordRequest(null);
                    }}
                    onCancel={() => {
                        passwordRequest.callback(new Error('Password cancelled'));
                        setPasswordRequest(null);
                    }}
                />
            )}
        </div>
    );
}

export default LandingPage;
